import { keccak256, slice, stringToHex, type Abi } from "viem";

type ErrorAbiItem = {
  type: "error";
  name: string;
  inputs: readonly { name?: string; type: string }[];
};

const errorItem = (
  name: string,
  inputs: ErrorAbiItem["inputs"] = []
): ErrorAbiItem => ({
  type: "error",
  name,
  inputs,
});

const OZ_ERRORS: ErrorAbiItem[] = [
  errorItem("EnforcedPause"),
  errorItem("ExpectedPause"),
  errorItem("OwnableUnauthorizedAccount", [{ name: "account", type: "address" }]),
  errorItem("OwnableInvalidOwner", [{ name: "owner", type: "address" }]),
  errorItem("ERC20InsufficientBalance", [
    { name: "sender", type: "address" },
    { name: "balance", type: "uint256" },
    { name: "needed", type: "uint256" },
  ]),
  errorItem("ERC20InsufficientAllowance", [
    { name: "spender", type: "address" },
    { name: "allowance", type: "uint256" },
    { name: "needed", type: "uint256" },
  ]),
  errorItem("ERC20InvalidSpender", [{ name: "spender", type: "address" }]),
  errorItem("ERC20InvalidSender", [{ name: "sender", type: "address" }]),
  errorItem("ERC20InvalidReceiver", [{ name: "receiver", type: "address" }]),
  errorItem("ERC1155InsufficientBalance", [
    { name: "sender", type: "address" },
    { name: "balance", type: "uint256" },
    { name: "needed", type: "uint256" },
    { name: "tokenId", type: "uint256" },
  ]),
  errorItem("ERC1155MissingApprovalForAll", [
    { name: "operator", type: "address" },
    { name: "owner", type: "address" },
  ]),
  errorItem("ERC1155InvalidOperator", [{ name: "operator", type: "address" }]),
  errorItem("ERC1155InvalidReceiver", [{ name: "receiver", type: "address" }]),
  errorItem("ERC1155InvalidSender", [{ name: "sender", type: "address" }]),
];

const SOLIDITY_BUILTIN_ERRORS: ErrorAbiItem[] = [
  errorItem("Error", [{ name: "message", type: "string" }]),
  errorItem("Panic", [{ name: "code", type: "uint256" }]),
];

const GAME_ERROR_NAMES = [
  "AuctionEnded",
  "AuctionNotEnded",
  "BuyCooldownNotReset",
  "CarNotInSameCity",
  "CarNotOwned",
  "CommonMissionNotCompleted",
  "ContractNotAllowed",
  "HealthOverflow",
  "InvalidBid",
  "InvalidCashAmountForPrizeType",
  "InvalidDuration",
  "InvalidListingToken",
  "InvalidListingType",
  "InvalidPrice",
  "InvalidStartPrice",
  "InvalidSwapToken",
  "InvalidXpType",
  "ItemNotActive",
  "NoBids",
  "NoMysteryBoxFound",
  "NotACarItem",
  "NotAuthorized",
  "NotEnoughHealth",
  "NotEnoughHealthLeft",
  "NotHospitalOwner",
  "NotInSameCity",
  "NotOwner",
  "NotRaceCreator",
  "NotValidCity",
  "PerkActiveBlockingListing",
  "PlayerAlreadyHasActiveLobby",
  "PlayerCannotJoinOwnRace",
  "RaceAlreadyJoined",
  "RaceCooldown",
  "RaceDoesNotExist",
  "RaceNotPending",
  "RaceNotStarted",
  "ShouldReproduceHealth",
  "SmuggleMissionNotCompleted",
  "StartPriceMustBeGreaterThanZero",
  "TransferHelper",
  "UserInJail",
] as const;

const GAME_ERRORS: ErrorAbiItem[] = GAME_ERROR_NAMES.map((name) =>
  errorItem(name)
);

export const COMMON_ERRORS_ABI: readonly ErrorAbiItem[] = [
  ...OZ_ERRORS,
  ...SOLIDITY_BUILTIN_ERRORS,
  ...GAME_ERRORS,
];

const errorSignature = (item: ErrorAbiItem) =>
  `${item.name}(${item.inputs.map((input) => input.type).join(",")})`;

export const toErrorSelector = (signature: string) =>
  slice(keccak256(stringToHex(signature)), 0, 4);

export const ERROR_SELECTOR_TO_NAME: Record<string, string> = Object.fromEntries(
  COMMON_ERRORS_ABI.filter((item) => item.name !== "Error" && item.name !== "Panic").map(
    (item) => [toErrorSelector(errorSignature(item)).toLowerCase(), item.name]
  )
);

const toAbiItems = (abi: unknown): readonly unknown[] => {
  if (Array.isArray(abi)) return abi;
  if (abi && typeof abi === "object" && Array.isArray((abi as { abi?: unknown[] }).abi)) {
    return (abi as { abi: unknown[] }).abi;
  }
  return [];
};

export const withCommonErrors = (abi: unknown): Abi => {
  const items = toAbiItems(abi);
  const existing = new Set(
    items
      .filter(
        (item): item is { type?: string; name?: string } =>
          !!item && typeof item === "object"
      )
      .filter((item) => item.type === "error")
      .map((item) => item.name)
      .filter((name): name is string => !!name)
  );

  return [
    ...items,
    ...COMMON_ERRORS_ABI.filter((item) => !existing.has(item.name)),
  ] as Abi;
};
